import React, { useState } from 'react';
import { DOCUMENT_ICONS } from '../utils/config';
import googleDriveService from '../services/googleDrive';
import DocumentCreationModal from './DocumentCreation/DocumentCreationModal';
import TechnicalDocumentModal from './DocumentCreation/TechnicalDocumentModal';
import AsanaIntegration from './AsanaIntegration/AsanaIntegration';
import '../components/DocumentCreation/DocumentCreation.css';

const Sidebar = ({ sections, onDocumentSelect, selectedDocument, currentFolder, onDocumentCreated }) => {
  
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [expandedSubfolders, setExpandedSubfolders] = useState(new Set());
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTechDocModal, setShowTechDocModal] = useState(false);
  const [selectedFolderForCreation, setSelectedFolderForCreation] = useState(null);
  const [showCreateMenu, setShowCreateMenu] = useState(null);
  const [creatingFolder, setCreatingFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [hoveredItem, setHoveredItem] = useState(null);
  const [showDocMenu, setShowDocMenu] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editingDocName, setEditingDocName] = useState('');
  const [menuPositions, setMenuPositions] = useState({});
  const [showAsanaIntegration, setShowAsanaIntegration] = useState(false);

  // Keyboard shortcut to toggle sidebar (Ctrl/Cmd + \)
  React.useEffect(() => {
    const handleKeyboard = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === '\\') {
        e.preventDefault();
        toggleSidebar();
      }
    };

    const handleClickOutside = (e) => {
      if (!e.target.closest('.folder-actions')) {
        setShowCreateMenu(null);
      }
    };

    document.addEventListener('keydown', handleKeyboard);
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyboard);
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const toggleSection = (folderId) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedSections(newExpanded);
  };

  const toggleSubfolder = (subfolderId) => {
    const newExpanded = new Set(expandedSubfolders);
    if (newExpanded.has(subfolderId)) {
      newExpanded.delete(subfolderId);
    } else {
      newExpanded.add(subfolderId);
    }
    setExpandedSubfolders(newExpanded);
  };

  const handleDocumentClick = (document) => {
    onDocumentSelect(document);
    
    // Auto-collapse on mobile after selecting document
    if (window.innerWidth <= 768) {
      setIsCollapsed(true);
    }
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Dynamic positioning for dropdown menus
  const positionCreateMenu = (buttonElement) => {
    const rect = buttonElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const menuWidth = 140; // min-width from CSS
    const menuHeight = 200; // estimated height
    
    let left = rect.right + 4; // Default: right side
    let top = rect.top;
    
    // If menu would go off right edge, show on left side
    if (left + menuWidth > viewportWidth - 10) {
      left = rect.left - menuWidth - 4;
    }
    
    // If menu would go off bottom edge, move up
    if (top + menuHeight > viewportHeight - 10) {
      top = viewportHeight - menuHeight - 10;
    }
    
    // Ensure menu doesn't go off top edge
    if (top < 10) {
      top = 10;
    }
    
    return { left: `${left}px`, top: `${top}px` };
  };

  const getDocumentIcon = (mimeType) => {
    const docType = googleDriveService.getDocumentType(mimeType);
    return DOCUMENT_ICONS[docType];
  };

  const handleCreateDocumentInFolder = (folder) => {
    setSelectedFolderForCreation(folder);
    setShowCreateModal(true);
  };

  const handleCreateFolder = (parentFolder) => {
    console.log('📁 Creating folder in parent:', parentFolder);
    console.log('📁 Parent folder details:', {
      id: parentFolder.id,
      name: parentFolder.name,
      mimeType: parentFolder.mimeType
    });
    
    // For subfolders, we need to create in the current section
    // Find which section this subfolder belongs to
    const parentSection = sections.find(section => 
      section.subfolders && section.subfolders.some(sf => sf.id === parentFolder.id)
    );
    
    if (parentSection) {
      // This is a subfolder, show input in the parent section
      setCreatingFolder(parentSection.folder.id);
      // Store the actual parent folder ID for creation
      setSelectedFolderForCreation(parentFolder);
    } else {
      // This is a main folder
      setCreatingFolder(parentFolder.id);
      setSelectedFolderForCreation(parentFolder);
    }
    
    setNewFolderName('');
    setShowCreateMenu(null);
    
    // Auto-expand the parent section to show the new folder input
    const newExpanded = new Set(expandedSections);
    newExpanded.add(parentSection ? parentSection.folder.id : parentFolder.id);
    setExpandedSections(newExpanded);
  };


  const handleSaveNewFolder = async (parentFolderId) => {
    if (!newFolderName.trim()) return;
    
    try {
      // Use the actual selected folder for creation if it exists
      const actualParentId = selectedFolderForCreation ? selectedFolderForCreation.id : parentFolderId;
      
      console.log('🔄 Creating folder:', newFolderName.trim(), 'in parent:', actualParentId);
      console.log('📂 Parent folder info:', selectedFolderForCreation);
      
      // Create folder via Google Drive API
      const googleDriveService = (await import('../services/googleDrive.js')).default;
      const accessToken = await googleDriveService.getAccessToken();
      
      console.log('🔑 Using access token:', accessToken.substring(0, 20) + '...');
      
      const folderMetadata = {
        name: newFolderName.trim(),
        mimeType: 'application/vnd.google-apps.folder',
        parents: [actualParentId]
      };

      console.log('📤 Folder metadata:', folderMetadata);

      const response = await fetch('https://www.googleapis.com/drive/v3/files?supportsAllDrives=true', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...folderMetadata,
          supportsAllDrives: true
        })
      });

      console.log('📥 Response status:', response.status, response.statusText);

      if (response.ok) {
        const newFolder = await response.json();
        console.log('✅ Folder created successfully:', newFolder);
        
        // Add permissions for the domain
        try {
          const permissionResponse = await fetch(`https://www.googleapis.com/drive/v3/files/${newFolder.id}/permissions?supportsAllDrives=true`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              role: 'writer',
              type: 'domain',
              domain: 'drilldocs.com',
              allowFileDiscovery: true
            })
          });
          
          if (permissionResponse.ok) {
            console.log('✅ Permissions added successfully');
          } else {
            console.warn('⚠️ Failed to add permissions:', await permissionResponse.text());
          }
        } catch (permError) {
          console.warn('⚠️ Error adding permissions:', permError);
        }
        
        // Reset creation state
        setCreatingFolder(null);
        setNewFolderName('');
        setSelectedFolderForCreation(null);
        
        // Trigger refresh of folder contents
        if (onDocumentCreated) {
          onDocumentCreated({ type: 'folder', ...newFolder });
        }
        
        // Refresh the page to show new folder
        window.location.reload();
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to create folder:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        
        // Try to re-authenticate if it's a permission issue
        if (response.status === 403 && errorText.includes('insufficient')) {
          console.log('🔄 Permission error, trying re-authentication...');
          try {
            await googleDriveService.getAccessToken(true);
            alert('Please try creating the folder again after re-authentication.');
          } catch (reAuthError) {
            console.error('Re-auth failed:', reAuthError);
            alert('Authentication failed. Please sign out and sign in again.');
          }
        } else if (response.status === 404) {
          console.error('❌ Parent folder not found. This might be a shared drive issue.');
          alert('Cannot create folders in this location. Try creating in a different folder.');
        } else {
          alert(`Failed to create folder: ${response.status} ${response.statusText}\n${errorText}`);
        }
      }
    } catch (error) {
      console.error('❌ Error creating folder:', error);
      alert(`Failed to create folder: ${error.message}`);
    }
  };

  const handleCancelNewFolder = () => {
    setCreatingFolder(null);
    setNewFolderName('');
    setSelectedFolderForCreation(null);
  };

  const handleFolderNameKeyPress = (e, parentFolderId) => {
    if (e.key === 'Enter') {
      handleSaveNewFolder(parentFolderId);
    } else if (e.key === 'Escape') {
      handleCancelNewFolder();
    }
  };

  const handleStartEditDoc = (document) => {
    setEditingDoc(document.id);
    setEditingDocName(document.name.replace(/\.\w+$/, '')); // Remove file extension
    setShowDocMenu(null);
  };

  const handleSaveDocEdit = async (document) => {
    if (!editingDocName.trim() || editingDocName === document.name) {
      setEditingDoc(null);
      return;
    }

    try {
      const googleDriveService = (await import('../services/googleDrive.js')).default;
      const accessToken = await googleDriveService.getAccessToken();
      
      console.log('📝 Renaming document:', document.id, 'to:', editingDocName);
      
      const response = await fetch(`https://www.googleapis.com/drive/v3/files/${document.id}?supportsAllDrives=true`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editingDocName.trim()
        })
      });

      if (response.ok) {
        console.log('✅ Document renamed successfully');
        setEditingDoc(null);
        setEditingDocName('');
        window.location.reload(); // Refresh to show new name
      } else {
        const error = await response.text();
        console.error('❌ Failed to rename document:', error);
        alert('Failed to rename document');
      }
    } catch (error) {
      console.error('❌ Error renaming document:', error);
      alert('Failed to rename document');
    }
  };

  const handleCancelDocEdit = () => {
    setEditingDoc(null);
    setEditingDocName('');
  };

  const handleDocEditKeyPress = (e, document) => {
    if (e.key === 'Enter') {
      handleSaveDocEdit(document);
    } else if (e.key === 'Escape') {
      handleCancelDocEdit();
    }
  };

  // Recursive component for rendering subfolders
  const renderSubfolder = (subfolder, depth = 0) => {
    const indentStyle = { marginLeft: `${depth * 16}px` };
    
    return (
      <div key={subfolder.id} className="subfolder-section" style={indentStyle}>
        {/* Subfolder header */}
        <div 
          className="subfolder-header"
          onMouseEnter={() => setHoveredItem(`subfolder-${subfolder.id}`)}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <span 
            className={`sidebar-icon ${expandedSubfolders.has(subfolder.id) ? 'expanded' : ''}`}
            onClick={() => toggleSubfolder(subfolder.id)}
          >
            ▼
          </span>
          <svg className="doc-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6H12L10,4Z"/>
          </svg>
          <span 
            className="subfolder-name"
            onClick={() => toggleSubfolder(subfolder.id)}
          >
            {subfolder.name}
          </span>
          
          {/* Hover actions for subfolders */}
          {hoveredItem === `subfolder-${subfolder.id}` && (
            <div className="item-actions">
              <button 
                className="item-action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  const menuId = subfolder.id;
                  if (showCreateMenu === menuId) {
                    setShowCreateMenu(null);
                    setMenuPositions(prev => ({ ...prev, [menuId]: undefined }));
                  } else {
                    const position = positionCreateMenu(e.target);
                    setMenuPositions(prev => ({ ...prev, [menuId]: position }));
                    setShowCreateMenu(menuId);
                  }
                }}
                title="Create"
              >
                +
              </button>
              
              {/* Create menu for subfolders */}
              {showCreateMenu === subfolder.id && (
                <div 
                  className="create-menu"
                  style={menuPositions[subfolder.id] || {}}
                >
                  <button 
                    className="create-menu-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreateDocumentInFolder(subfolder);
                      setShowCreateMenu(null);
                    }}
                  >
                    Create Document
                  </button>
                  <button 
                    className="create-menu-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFolderForCreation(subfolder);
                      setShowTechDocModal(true);
                      setShowCreateMenu(null);
                    }}
                  >
                    Create Technical Document
                  </button>
                  <button 
                    className="create-menu-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFolderForCreation(subfolder);
                      setShowAsanaIntegration(true);
                      setShowCreateMenu(null);
                    }}
                  >
                    Import from Asana
                  </button>
                  <button 
                    className="create-menu-item delete-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteFolder(subfolder);
                      setShowCreateMenu(null);
                    }}
                  >
                    Delete Folder
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Subfolder content (shown when expanded) */}
        {expandedSubfolders.has(subfolder.id) && (
          <div className="subfolder-items">
            {/* Documents in this subfolder */}
            {subfolder.documents && subfolder.documents.length > 0 && subfolder.documents.map((document) => (
              <div 
                key={document.id}
                className={`document-item ${selectedDocument?.id === document.id ? 'active' : ''}`}
                onClick={() => handleDocumentClick(document)}
                onMouseEnter={() => setHoveredItem(`doc-${document.id}`)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <svg className="doc-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d={getDocumentIcon(document.mimeType)} />
                </svg>
                {editingDoc === document.id ? (
                  <input
                    type="text"
                    value={editingDocName}
                    onChange={(e) => setEditingDocName(e.target.value)}
                    onKeyDown={(e) => handleDocEditKeyPress(e, document)}
                    onBlur={() => handleSaveDocEdit(document)}
                    className="inline-edit-input"
                    autoFocus
                  />
                ) : (
                  <span className="document-name">{document.name}</span>
                )}
                
                {/* Hover actions for documents */}
                {hoveredItem === `doc-${document.id}` && (
                  <div className="item-actions">
                    <button 
                      className="item-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        const menuId = `doc-${document.id}`;
                        if (showDocMenu === document.id) {
                          setShowDocMenu(null);
                          setMenuPositions(prev => ({ ...prev, [menuId]: undefined }));
                        } else {
                          const position = positionCreateMenu(e.target);
                          setMenuPositions(prev => ({ ...prev, [menuId]: position }));
                          setShowDocMenu(document.id);
                        }
                      }}
                      title="Actions"
                    >
                      ⋯
                    </button>
                    
                    {/* Document actions menu */}
                    {showDocMenu === document.id && (
                      <div 
                        className="doc-actions-menu"
                        style={menuPositions[`doc-${document.id}`] || {}}
                      >
                        <button 
                          className="create-menu-item"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartEditDoc(document);
                          }}
                        >
                          Rename
                        </button>
                        <button 
                          className="create-menu-item"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`https://docs.google.com/document/d/${document.id}/edit`, '_blank');
                            setShowDocMenu(null);
                          }}
                        >
                          Open in Docs
                        </button>
                        <button 
                          className="create-menu-item delete-item"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDocument(document);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            
            {/* Recursively render nested subfolders */}
            {subfolder.subfolders && subfolder.subfolders.length > 0 && 
              subfolder.subfolders.map((nestedSubfolder) => renderSubfolder(nestedSubfolder, depth + 1))
            }
            
            {/* Empty message if no content */}
            {(!subfolder.documents || subfolder.documents.length === 0) && 
             (!subfolder.subfolders || subfolder.subfolders.length === 0) && (
              <div className="empty-folder-message">
                <span>Empty subfolder</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleDeleteDocument = async (document) => {
    if (!window.confirm(`Delete "${document.name}"?\n\nThis action cannot be undone.`)) {
      setShowDocMenu(null);
      return;
    }

    try {
      const googleDriveService = (await import('../services/googleDrive.js')).default;
      const accessToken = await googleDriveService.getAccessToken();
      
      console.log('🗑️ Deleting document:', document.id, document.name);
      console.log('📄 Document details:', document);
      
      // Try to trash the file instead of permanently deleting
      const updateBody = {
        trashed: true
      };
      
      const response = await fetch(`https://www.googleapis.com/drive/v3/files/${document.id}?supportsAllDrives=true`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateBody)
      });

      if (response.ok) {
        console.log('✅ Document moved to trash successfully');
        setShowDocMenu(null);
        
        // Clear selected document if it was deleted
        if (selectedDocument?.id === document.id) {
          onDocumentSelect(null);
        }
        
        // Refresh the page to show updated list
        window.location.reload();
      } else if (response.status === 404) {
        // If file not found, it might already be deleted or we don't have access
        const errorData = await response.json();
        console.error('❌ Document not found:', errorData);
        
        // Check if we have the right permissions
        if (errorData.error?.message?.includes('File not found')) {
          alert('This document cannot be deleted. It may have already been removed or you may not have permission to delete it.');
        } else {
          alert(`Failed to delete document: ${errorData.error?.message || 'Unknown error'}`);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to delete document:', errorText);
        alert(`Failed to delete document: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('❌ Error deleting document:', error);
      alert(`Failed to delete document: ${error.message}`);
    }
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Move folder "${folder.name}" and all its contents to trash?\n\nYou can restore it from Google Drive trash if needed.`)) {
      return;
    }

    try {
      const googleDriveService = (await import('../services/googleDrive.js')).default;
      const accessToken = await googleDriveService.getAccessToken();
      
      console.log('🗑️ Moving folder to trash:', folder.id, folder.name);
      console.log('📁 Folder details:', folder);
      
      // Move to trash instead of permanently deleting
      const updateBody = {
        trashed: true
      };
      
      const response = await fetch(`https://www.googleapis.com/drive/v3/files/${folder.id}?supportsAllDrives=true`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateBody)
      });

      if (response.ok) {
        console.log('✅ Folder moved to trash successfully');
        
        // Refresh the page to show updated list
        window.location.reload();
      } else if (response.status === 404) {
        const errorData = await response.json();
        console.error('❌ Folder not found:', errorData);
        alert('This folder cannot be deleted. It may have already been removed or you may not have permission to delete it.');
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to delete folder:', errorText);
        alert(`Failed to delete folder: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('❌ Error deleting folder:', error);
      alert(`Failed to delete folder: ${error.message}`);
    }
  };

  return (
    <>
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>

      {/* Sidebar Content */}
      <div className="sidebar-content" style={{ display: isCollapsed ? 'none' : 'block' }}>

        {sections.length === 0 ? (
          <div className="no-documents">
            <p>No documents found in this process</p>
          </div>
        ) : (
          sections.map((section) => (
            <div key={section.folder.id} className="sidebar-section">
              
              <div className="sidebar-header">
                <span className={`sidebar-icon ${expandedSections.has(section.folder.id) ? 'expanded' : ''}`}>
                  ▼
                </span>
                <div 
                  className="folder-name-area"
                  onClick={() => toggleSection(section.folder.id)}
                  style={{ flex: 1, display: 'flex', alignItems: 'center' }}
                >
                  <span>{section.folder.name}</span>
                </div>
                <div className="folder-actions">
                  <button 
                    className="folder-create-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('➕ Plus button clicked for folder:', section.folder.name);
                      const menuId = section.folder.id;
                      if (showCreateMenu === menuId) {
                        setShowCreateMenu(null);
                        setMenuPositions(prev => ({ ...prev, [menuId]: undefined }));
                      } else {
                        const position = positionCreateMenu(e.target);
                        setMenuPositions(prev => ({ ...prev, [menuId]: position }));
                        setShowCreateMenu(menuId);
                      }
                    }}
                    title="Create"
                  >
                    +
                  </button>
                  {showCreateMenu === section.folder.id && (
                    <div 
                      className="create-menu" 
                      style={menuPositions[section.folder.id] || {}}
                    >
                      <button 
                        className="create-menu-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateDocumentInFolder(section.folder);
                          setShowCreateMenu(null);
                        }}
                      >
                        Create Document
                      </button>
                      <button 
                        className="create-menu-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFolderForCreation(section.folder);
                          setShowTechDocModal(true);
                          setShowCreateMenu(null);
                        }}
                      >
                        Create Technical Document
                      </button>
                      <button 
                        className="create-menu-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('📁 Create Folder clicked for:', section.folder.name);
                          handleCreateFolder(section.folder);
                        }}
                      >
                        Create Folder
                      </button>
                      <button 
                        className="create-menu-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFolderForCreation(section.folder);
                          setShowAsanaIntegration(true);
                          setShowCreateMenu(null);
                        }}
                      >
                        Import from Asana
                      </button>
                      <button 
                        className="create-menu-item delete-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFolder(section.folder);
                          setShowCreateMenu(null);
                        }}
                      >
                        Delete Folder
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {expandedSections.has(section.folder.id) && (
                <div className="sidebar-items">
                  {/* New Folder Input */}
                  {creatingFolder === section.folder.id && (
                    <div className="new-folder-input">
                      <div className="folder-input-container">
                        <svg className="doc-icon" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6H12L10,4Z"/>
                        </svg>
                        <input
                          type="text"
                          value={newFolderName}
                          onChange={(e) => setNewFolderName(e.target.value)}
                          onKeyDown={(e) => handleFolderNameKeyPress(e, section.folder.id)}
                          onBlur={() => handleCancelNewFolder()}
                          placeholder="Folder name"
                          className="folder-name-input"
                          autoFocus
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Subfolders - using recursive rendering */}
                  {section.subfolders && section.subfolders.length > 0 && 
                    section.subfolders.map((subfolder) => renderSubfolder(subfolder))
                  }
                  
                  {/* Show message if folder is empty */}
                  {section.documents.length === 0 && (!section.subfolders || section.subfolders.length === 0) && creatingFolder !== section.folder.id && (
                    <div className="empty-folder-message">
                      <span>Empty folder</span>
                    </div>
                  )}
                  
                  {/* Documents */}
                  {section.documents.map((document) => (
                    <div 
                      key={document.id}
                      className={`document-item ${selectedDocument?.id === document.id ? 'active' : ''}`}
                      onClick={() => handleDocumentClick(document)}
                      onMouseEnter={() => setHoveredItem(`doc-${document.id}`)}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      <svg className="doc-icon" viewBox="0 0 24 24" fill="currentColor">
                        <path d={getDocumentIcon(document.mimeType)} />
                      </svg>
                      {editingDoc === document.id ? (
                        <input
                          type="text"
                          value={editingDocName}
                          onChange={(e) => setEditingDocName(e.target.value)}
                          onKeyDown={(e) => handleDocEditKeyPress(e, document)}
                          onBlur={() => handleSaveDocEdit(document)}
                          className="inline-edit-input"
                          autoFocus
                        />
                      ) : (
                        <span className="document-name">{document.name}</span>
                      )}
                      
                      {/* Hover actions for documents */}
                      {hoveredItem === `doc-${document.id}` && (
                        <div className="item-actions">
                          <button 
                            className="item-action-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              const menuId = `doc-${document.id}`;
                              if (showDocMenu === document.id) {
                                setShowDocMenu(null);
                                setMenuPositions(prev => ({ ...prev, [menuId]: undefined }));
                              } else {
                                const position = positionCreateMenu(e.target);
                                setMenuPositions(prev => ({ ...prev, [menuId]: position }));
                                setShowDocMenu(document.id);
                              }
                            }}
                            title="Actions"
                          >
                            ⋯
                          </button>
                          
                          {/* Document actions menu */}
                          {showDocMenu === document.id && (
                            <div 
                              className="doc-actions-menu"
                              style={menuPositions[`doc-${document.id}`] || {}}
                            >
                              <button 
                                className="create-menu-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStartEditDoc(document);
                                }}
                              >
                                Rename
                              </button>
                              <button 
                                className="create-menu-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`https://docs.google.com/document/d/${document.id}/edit`, '_blank');
                                  setShowDocMenu(null);
                                }}
                              >
                                Open in Docs
                              </button>
                              <button 
                                className="create-menu-item delete-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteDocument(document);
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

            </div>
          ))
        )}
      </div>

      {/* Collapsed State - Show minimal info */}
      {isCollapsed && (
        <div className="sidebar-collapsed-content">
          <div className="collapsed-indicator">
            <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '20px', height: '20px' }}>
              <path d="M3,6H21V8H3V6M3,11H21V13H3V11M3,16H21V18H3V16Z"/>
            </svg>
            <div className="collapsed-count">
              {sections.reduce((total, section) => total + section.documents.length, 0)} docs
            </div>
          </div>
        </div>
      )}

      {/* Document Creation Modal */}
      <DocumentCreationModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedFolderForCreation(null);
        }}
        currentFolder={selectedFolderForCreation}
        onDocumentCreated={(document) => {
          setShowCreateModal(false);
          setSelectedFolderForCreation(null);
          if (onDocumentCreated) {
            onDocumentCreated(document);
          }
        }}
      />

      {/* Technical Document Creation Modal */}
      <TechnicalDocumentModal
        isOpen={showTechDocModal}
        onClose={() => {
          setShowTechDocModal(false);
          setSelectedFolderForCreation(null);
        }}
        currentFolder={selectedFolderForCreation}
        onDocumentCreated={(document) => {
          setShowTechDocModal(false);
          setSelectedFolderForCreation(null);
          if (onDocumentCreated) {
            onDocumentCreated(document);
          }
        }}
      />
    </div>
    {/* Toggle Button - OUTSIDE sidebar for visibility */}
    <button 
      className={`sidebar-toggle ${isCollapsed ? 'sidebar-collapsed' : ''}`}
      onClick={toggleSidebar}
      title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      style={{
        position: 'fixed',
        left: isCollapsed ? '60px' : '300px',
        transition: 'left 0.3s ease'
      }}
    >
      {isCollapsed ? '▶' : '◀'}
    </button>
    </>
  );
};

export default Sidebar;